/**
 * One loader per connectable source, shared by bootstrap
 * (`loadAllDataSWR.ts`) and per-source retry (`retrySourceLoad.ts`) so both
 * flows fetch, store the raw entity, and map it into `AttentionItem[]` the
 * same way. Adding a provider means adding one entry to
 * `ATTENTION_SOURCE_LOADERS` here instead of a new block in each of those
 * two files — see `docs/ADDING_A_PROVIDER.md`.
 */

import {
  mapGitHubPRToAttentionItem,
  mapGmailThreadToAttentionItem,
  mapJiraIssueToAttentionItem,
  mapSlackThreadToAttentionItem,
} from '../domain/mappers';
import type {
  AttentionItem,
  ApiEnvelope,
  CurrentUserIdentity,
  GitHubPullRequest,
  GmailMessage,
  GmailThread,
  JiraIssue,
  SlackThread,
} from '../domain/types';
import { CONNECTABLE_SOURCES, type ConnectableSource } from '../domain/providerIds';
import { gitHubClient, gmailClient, jiraClient, slackClient } from '../services/clients';

/** The subset of state setters a source loader needs to store its raw entity list. */
export interface SourceLoaderDeps {
  setRawJiraIssues: (value: JiraIssue[]) => void;
  setRawPullRequests: (value: GitHubPullRequest[]) => void;
  setRawSlackThreads: (value: SlackThread[]) => void;
  setRawGmailMessages: (value: GmailMessage[]) => void;
}

export interface AttentionSourceLoader<TRaw> {
  source: ConnectableSource;
  fetch: () => Promise<ApiEnvelope<TRaw[]>>;
  setRaw: (deps: SourceLoaderDeps, raw: TRaw[]) => void;
  /** Maps raw entities to items, preserving `st`/`ai`/`next` from the matching previous item. */
  toItems: (
    raw: TRaw[],
    prevItems: AttentionItem[],
    currentUser?: CurrentUserIdentity,
  ) => AttentionItem[];
  /** Where newly-mapped items land relative to the rest of the list (matches pre-registry ordering). */
  placement: 'prepend' | 'append';
}

const jiraLoader: AttentionSourceLoader<JiraIssue> = {
  source: 'jira',
  fetch: () => jiraClient.getIssues(),
  setRaw: (deps, raw) => deps.setRawJiraIssues(raw),
  toItems: (raw, prevItems) =>
    raw.map((issue) => {
      const original = prevItems.find((it) => it.ticket === issue.key);
      return mapJiraIssueToAttentionItem(
        issue,
        original?.st,
        issue.zuneloMeta?.ai ?? original?.ai,
        issue.zuneloMeta?.next ?? original?.next,
      );
    }),
  placement: 'prepend',
};

const githubLoader: AttentionSourceLoader<GitHubPullRequest> = {
  source: 'github',
  fetch: () => gitHubClient.getPullRequests(),
  setRaw: (deps, raw) => deps.setRawPullRequests(raw),
  toItems: (raw, prevItems, currentUser) =>
    raw.map((pr) => {
      const original = prevItems.find((it) => it.primary === `#${pr.number}` || it.id === pr.id);
      return mapGitHubPRToAttentionItem(
        pr,
        original?.st,
        original?.ai,
        original?.next,
        currentUser,
      );
    }),
  placement: 'append',
};

const slackLoader: AttentionSourceLoader<SlackThread> = {
  source: 'slack',
  fetch: () => slackClient.getThreads(),
  setRaw: (deps, raw) => deps.setRawSlackThreads(raw),
  toItems: (raw, prevItems) =>
    raw.map((thread) => {
      const original = prevItems.find((it) => it.primary === thread.channel.name);
      return mapSlackThreadToAttentionItem(
        thread,
        thread.zuneloMeta?.status,
        original?.ai,
        original?.next,
      );
    }),
  placement: 'append',
};

const gmailLoader: AttentionSourceLoader<GmailThread> = {
  source: 'gmail',
  fetch: () => gmailClient.getMessages(),
  setRaw: (deps, raw) => {
    // If raw contains threads, extract messages for setRawGmailMessages backward compatibility
    const allMessages = raw.flatMap((t) => t.messages || []);
    deps.setRawGmailMessages(allMessages);
  },
  toItems: (raw, prevItems) =>
    raw.map((thread) => {
      const original = prevItems.find((it) => it.id === thread.id);
      return mapGmailThreadToAttentionItem(thread, original?.st, original?.ai, original?.next);
    }),
  placement: 'append',
};

export const ATTENTION_SOURCE_LOADERS: Record<ConnectableSource, AttentionSourceLoader<unknown>> = {
  jira: jiraLoader as unknown as AttentionSourceLoader<unknown>,
  github: githubLoader as unknown as AttentionSourceLoader<unknown>,
  slack: slackLoader as unknown as AttentionSourceLoader<unknown>,
  gmail: gmailLoader as unknown as AttentionSourceLoader<unknown>,
};

/**
 * Applies one source's raw fetch result: stores it, maps it against the
 * freshest item list (read via the `setItems` functional update, not a
 * snapshot taken before the call), splices it into `items`, and clears its
 * error.
 */
export function applyAttentionSource(
  deps: {
    setItems: (updater: (prev: AttentionItem[]) => AttentionItem[]) => void;
    setErrors?: (
      updater: (prev: Record<string, string | null>) => Record<string, string | null>,
    ) => void;
    currentUser?: CurrentUserIdentity;
  } & SourceLoaderDeps,
  source: ConnectableSource,
  raw: unknown[],
): void {
  const loader = ATTENTION_SOURCE_LOADERS[source];
  loader.setRaw(deps, raw);
  deps.setItems((prev) => {
    const mapped = loader.toItems(raw, prev, deps.currentUser);
    const rest = prev.filter((it) => it.source !== source);
    return loader.placement === 'prepend' ? [...mapped, ...rest] : [...rest, ...mapped];
  });
  deps.setErrors?.((prev) => ({ ...prev, [source]: null }));
}

export { CONNECTABLE_SOURCES };
